import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { PhaseTimerProvider } from "@/contexts/PhaseTimerContext";
import { useApplianceImageBackfill } from "@/hooks/useApplianceImageBackfill";
import { RequireCompany } from "./RequireCompany";
import { useCompanyNavTabs, type NavTabId } from "@/hooks/useCompanyNavTabs";
import { useLocale } from "@/hooks/useLocale";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";

interface AppLayoutProps {
  children: React.ReactNode;
  /** Render only the app providers, without the main app sidebar chrome. */
  bare?: boolean;
}

function pathToTab(path: string): NavTabId | null {
  if (path === "/") return "search";
  if (path.startsWith("/work-orders")) return "work-orders";
  if (path.startsWith("/inventory")) return "inventory";
  if (path.startsWith("/scanner")) return "scanner";
  if (path.startsWith("/store")) return "store";
  if (path.startsWith("/profile")) return "profile";
  if (path.startsWith("/admin")) return "admin";
  return null;
}

const TAB_FALLBACK_PATH: Record<NavTabId, string> = {
  "search": "/",
  "work-orders": "/work-orders",
  "inventory": "/inventory",
  "scanner": "/scanner",
  "store": "/store",
  "profile": "/profile",
  "admin": "/admin",
};

function NavTabGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isTabEnabled, ready } = useCompanyNavTabs();

  useEffect(() => {
    if (!ready) return;
    const tab = pathToTab(location.pathname);
    if (!tab) return;
    if (isTabEnabled(tab)) return;
    const order: NavTabId[] = ["search", "work-orders", "inventory", "scanner", "store", "profile"];
    const target = order.find((t) => isTabEnabled(t)) ?? "profile";
    toast.info("This feature is disabled for your company");
    navigate(TAB_FALLBACK_PATH[target], { replace: true });
  }, [location.pathname, ready, isTabEnabled, navigate]);

  return <>{children}</>;
}

export function AppLayout({ children, bare = false }: AppLayoutProps) {
  // Initialise the locale hook so the user's saved language is restored on login.
  useLocale();
  // Once per session, refresh any stored appliance images that don't meet
  // the strict ≥75%-coverage quality bar.
  useApplianceImageBackfill();

  if (bare) {
    return (
      <RequireCompany>
        <PhaseTimerProvider>
          <NavTabGuard>{children}</NavTabGuard>
        </PhaseTimerProvider>
      </RequireCompany>
    );
  }

  return (
    <RequireCompany>
      <PhaseTimerProvider>
        <NavTabGuard>
          <SidebarProvider
            defaultWidth={280}
            minWidth={220}
            maxWidth={330}
            style={
              {
                "--sidebar-width-icon": "4rem",
              } as React.CSSProperties
            }
          >

            <AppSidebar />
            <div className="flex-1 flex flex-col min-w-0 min-h-svh">
              {/* Mobile top bar with sidebar trigger */}
              <header className="md:hidden sticky top-0 z-30 flex items-center h-12 px-3 bg-background/95 backdrop-blur border-b">
                <SidebarTrigger />
              </header>
              <main className="flex-1 min-w-0">{children}</main>
            </div>
          </SidebarProvider>
        </NavTabGuard>
      </PhaseTimerProvider>
    </RequireCompany>
  );
}
